import { useState, useRef, useCallback, useMemo } from 'react';

import type { IItemDataResponse } from '@/pages/PurchasesContainer/RequestPerHand/api/HandRequest.interface';

// Helper function to select text in an input with debounce
const selectText = (element: HTMLInputElement | null) => {
  if (!element) return;
  // Use requestAnimationFrame for better performance
  requestAnimationFrame(() => {
    element.select();
  });
};

export function useFormHandlers() {
  // State - grouped by related fields for better organization
  const [inputs, setInputs] = useState({
    qty: '1',
    tax: '0',
    discount: '20',
    specialDiscount: '',
    bonus: '0',
    bno: '',
    expireDate: '',
  });

  // Refs
  const qtyRef = useRef<HTMLInputElement>(null);
  const taxRef = useRef<HTMLInputElement>(null);
  const discountRef = useRef<HTMLInputElement>(null);
  const specialDiscountRef = useRef<HTMLInputElement>(null);
  const bonusRef = useRef<HTMLInputElement>(null);
  const bnoRef = useRef<HTMLInputElement>(null);
  const expireDateRef = useRef<HTMLInputElement>(null);
  const submitButtonRef = useRef<HTMLButtonElement>(null);

  // Input validators - using useMemo for performance
  const validators = useMemo(
    () => ({
      qty: (value: string) => value === '' || /^\d*$/.test(value),
      tax: (value: string) => value === '' || /^\d*\.?\d{0,2}$/.test(value),
      discount: (value: string) =>
        value === '' || /^\d*\.?\d{0,3}$/.test(value),
      specialDiscount: (value: string) =>
        value === '' || /^[\d\s+.]*$/.test(value),
      bonus: (value: string) => value === '' || /^\d*$/.test(value),
      bno: (value: string) => value === '' || /^[a-zA-Z0-9]*$/.test(value),
    }),
    [],
  );

  // Custom handler for BNO to accept alphanumeric values
  const handleBnoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      // Use a simpler, direct approach
      if (/^[a-zA-Z0-9]*$/.test(value)) {
        setInputs((prev) => ({ ...prev, bno: value }));
      } else {
      }
    },
    [],
  );

  // Generic change handler - memoized with useCallback
  const handleChange = useCallback(
    (field: keyof typeof inputs) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const validator = validators[field as keyof typeof validators];

        if (validator && validator(value)) {
          setInputs((prev) => ({ ...prev, [field]: value }));
        }
      },
    [validators],
  );

  // Round helper
  const round = useCallback((num: number, decimals: number): number => {
    const factor = 10 ** decimals;
    return Math.round(num * factor) / factor;
  }, []);

  // Calculate compound discount - memoized
  const calculateCompoundDiscount = useCallback(
    (discounts: string): number => {
      try {
        // Split the input by + and trim whitespace
        const discountValues = discounts
          .split('+')
          .map((d) => parseFloat(d.trim()))
          .filter((d) => !isNaN(d));

        if (discountValues.length === 0) return 0;

        // Calculate compound discount using the formula:
        // 100 * (1 - (1 - d1/100) * (1 - d2/100) * ...)
        const result =
          100 *
          (1 - discountValues.reduce((acc, curr) => acc * (1 - curr / 100), 1));

        return round(result, 2);
      } catch {
        return 0;
      }
    },
    [round],
  );

  // Blur handlers - memoized with useCallback
  const handleQtyBlur = useCallback(() => {
    setInputs((prev) => {
      const numericValue = parseInt(prev.qty, 10);
      return {
        ...prev,
        qty:
          isNaN(numericValue) || numericValue <= 1 ? '1' : String(numericValue),
      };
    });
  }, []);

  const handleTaxBlur = useCallback(() => {
    setInputs((prev) => {
      if (prev.tax === '') return { ...prev, tax: '0' };

      const numericValue = parseFloat(prev.tax);
      return {
        ...prev,
        tax:
          !isNaN(numericValue) && numericValue >= 0
            ? numericValue.toString()
            : '0',
      };
    });
  }, []);

  const handleDiscountBlur = useCallback(() => {
    setInputs((prev) => {
      const numericValue = parseFloat(prev.discount);
      if (isNaN(numericValue) || numericValue < 0) {
        return { ...prev, discount: '0' };
      } else if (numericValue > 100) {
        return { ...prev, discount: '100' };
      }
      return prev;
    });
  }, []);

  const handleSpecialDiscountBlur = useCallback(() => {
    setInputs((prev) => {
      const value = prev.specialDiscount;
      if (value === '') return prev;

      if (value.includes('+')) {
        const compoundDiscount = calculateCompoundDiscount(value);
        return { ...prev, specialDiscount: String(compoundDiscount) };
      }
      return prev;
    });
  }, [calculateCompoundDiscount]);

  const handleBonusBlur = useCallback(() => {
    setInputs((prev) => {
      const value = prev.bonus;
      if (value === '') return { ...prev, bonus: '0' };

      const numericValue = parseInt(value, 10);
      return {
        ...prev,
        bonus:
          isNaN(numericValue) || numericValue < 0 ? '0' : String(numericValue),
      };
    });
  }, []);

  const handleBnoBlur = useCallback(() => {
    // No transformation needed
  }, []);

  const handleExpireDateBlur = useCallback(() => {
    setInputs((prev) => {
      const value = prev.expireDate;

      if (value === '') {
        const now = new Date();
        const nextYear = now.getFullYear() + 1;
        const nextMonth = now.getMonth() + 1;
        return {
          ...prev,
          expireDate: `${nextMonth.toString().padStart(2, '0')}/${nextYear}`,
        };
      }

      const [month, year] = value.split('/');
      if (!month || !year || year.length !== 4) {
        const now = new Date();
        const nextYear = now.getFullYear() + 1;
        const nextMonth = now.getMonth() + 1;
        return {
          ...prev,
          expireDate: `${nextMonth.toString().padStart(2, '0')}/${nextYear}`,
        };
      }

      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      const monthNum = parseInt(month, 10);
      if (monthNum < 1 || monthNum > 12) {
        return {
          ...prev,
          expireDate: `${currentMonth.toString().padStart(2, '0')}/${currentYear + 1}`,
        };
      }

      const yearNum = parseInt(year, 10);
      if (yearNum < currentYear || yearNum > currentYear + 10) {
        return {
          ...prev,
          expireDate: `${currentMonth.toString().padStart(2, '0')}/${currentYear + 1}`,
        };
      }

      if (yearNum === currentYear && monthNum <= currentMonth) {
        return {
          ...prev,
          expireDate: `${monthNum.toString().padStart(2, '0')}/${currentYear + 1}`,
        };
      }

      return {
        ...prev,
        expireDate: `${monthNum.toString().padStart(2, '0')}/${yearNum}`,
      };
    });
  }, []);

  // Focus handlers
  const handleFocusSelect = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      selectText(e.target);
    },
    [],
  );

  const handleExpireDateFocus = useCallback(
    (
      e: React.FocusEvent<HTMLInputElement>,
      itemData: IItemDataResponse | null,
    ) => {
      if (itemData?.itemLastIncoming?.ExpireDate) {
        const expireDate = new Date(itemData.itemLastIncoming.ExpireDate);
        const month = (expireDate.getMonth() + 1).toString().padStart(2, '0');
        const year = expireDate.getFullYear();
        setInputs((prev) => ({ ...prev, expireDate: `${month}/${year}` }));
      }
      selectText(e.target);
    },
    [],
  );

  // Navigation focus - memoized
  const fnToGoToQty = useCallback(() => {
    if (qtyRef.current) {
      qtyRef.current.focus();
      selectText(qtyRef.current);
    }
  }, []);

  const fnToGoToDiscount = useCallback(() => {
    if (discountRef.current) {
      discountRef.current.focus();
      selectText(discountRef.current);
    }
  }, []);

  // Keyboard navigation - memoized
  const handleQtyKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        qtyRef.current?.blur();
        if (e.shiftKey) {
          // Do nothing or focus on previous field
        } else {
          taxRef.current?.focus();
          selectText(taxRef.current);
        }
      }
    },
    [],
  );

  const handleTaxKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleTaxBlur();
        taxRef.current?.blur();
        if (e.shiftKey) {
          qtyRef.current?.focus();
          selectText(qtyRef.current);
        } else {
          discountRef.current?.focus();
          selectText(discountRef.current);
        }
      }
    },
    [handleTaxBlur],
  );

  const handleDiscountKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        discountRef.current?.blur();
        if (e.shiftKey) {
          taxRef.current?.focus();
          selectText(taxRef.current);
        } else {
          specialDiscountRef.current?.focus();
          selectText(specialDiscountRef.current);
        }
      }
    },
    [],
  );

  const handleSpecialDiscountKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSpecialDiscountBlur();
        specialDiscountRef.current?.blur();

        if (e.shiftKey) {
          discountRef.current?.focus();
          selectText(discountRef.current);
        } else {
          bonusRef.current?.focus();
          selectText(bonusRef.current);
        }
      }
    },
    [handleSpecialDiscountBlur],
  );

  const handleBonusKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === '.') {
        e.preventDefault();
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        handleBonusBlur();
        bonusRef.current?.blur();

        if (e.shiftKey) {
          specialDiscountRef.current?.focus();
          selectText(specialDiscountRef.current);
        } else {
          bnoRef.current?.focus();
          selectText(bnoRef.current);
        }
      }
    },
    [handleBonusBlur],
  );

  const handleBnoKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        bnoRef.current?.blur();

        if (e.shiftKey) {
          bonusRef.current?.focus();
          selectText(bonusRef.current);
        } else {
          expireDateRef.current?.focus();
          if (expireDateRef.current) {
            expireDateRef.current.setSelectionRange(3, 7);
          }
        }
      }
    },
    [],
  );

  const handleExpireDateChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;

      if (value === '') {
        const now = new Date();
        const nextYear = now.getFullYear() + 1;
        const nextMonth = now.getMonth() + 1;
        setInputs((prev) => ({
          ...prev,
          expireDate: `${nextMonth.toString().padStart(2, '0')}/${nextYear}`,
        }));
        return;
      }

      const digits = value.replace(/\D/g, '');
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      if (digits.length <= 2) {
        const month = parseInt(digits, 10);
        if (month >= 1 && month <= 12) {
          setInputs((prev) => ({
            ...prev,
            expireDate: digits.padStart(2, '0'),
          }));
        }
      } else if (digits.length <= 6) {
        const month = parseInt(digits.slice(0, 2), 10);
        const year = parseInt(digits.slice(2), 10);

        if (month < 1 || month > 12) {
          return;
        }

        if (year < currentYear || year > currentYear + 10) {
          return;
        }

        if (year === currentYear && month <= currentMonth) {
          const nextYear = currentYear + 1;
          setInputs((prev) => ({
            ...prev,
            expireDate: `${month.toString().padStart(2, '0')}/${nextYear}`,
          }));
          return;
        }

        setInputs((prev) => ({
          ...prev,
          expireDate: `${month.toString().padStart(2, '0')}/${year}`,
        }));
      }
    },
    [],
  );

  const handleExpireDateKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        expireDateRef.current?.blur();

        if (e.shiftKey) {
          bnoRef.current?.focus();
          selectText(bnoRef.current);
        } else {
          submitButtonRef.current?.focus();
        }
      } else if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        const input = e.currentTarget;
        input.setSelectionRange(0, 2);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const input = e.currentTarget;
        const value = input.value;
        const [month, year] = value.split('/');

        // Parse current values
        const currentMonth = parseInt(month, 10);
        const currentYear = parseInt(year, 10);

        if (isNaN(currentMonth) || isNaN(currentYear)) return;

        let newMonth: number;
        let newYear: number;

        // Update month and handle year changes automatically
        if (e.key === 'ArrowUp') {
          if (currentMonth === 12) {
            newMonth = 1;
            newYear = currentYear + 1;
          } else {
            newMonth = currentMonth + 1;
            newYear = currentYear;
          }
        } else {
          // ArrowDown
          if (currentMonth === 1) {
            newMonth = 12;
            newYear = currentYear - 1;
          } else {
            newMonth = currentMonth - 1;
            newYear = currentYear;
          }
        }

        // Ensure the year is within valid range
        const now = new Date();
        const minYear = now.getFullYear();
        const maxYear = now.getFullYear() + 10;

        newYear = Math.max(minYear, Math.min(maxYear, newYear));

        // Update the input value
        setInputs((prev) => ({
          ...prev,
          expireDate: `${newMonth.toString().padStart(2, '0')}/${newYear}`,
        }));

        // Keep focus on the month part for better UX
        input.setSelectionRange(0, 2);
      }
    },
    [],
  );

  // Reset form - memoized
  const resetForm = useCallback(() => {
    setInputs({
      qty: '1',
      tax: '0',
      discount: '20',
      specialDiscount: '',
      bonus: '0',
      bno: '',
      expireDate: '',
    });
  }, []);

  // Extract individual values for compatibility
  const {
    qty: qtyInput,
    tax: taxInput,
    discount: discountInput,
    specialDiscount: specialDiscountInput,
    bonus: bonusInput,
    bno: bnoInput,
    expireDate,
  } = inputs;

  // Getter functions to avoid unnecessary object destructions
  const setQtyInput = useCallback((value: string) => {
    setInputs((prev) => ({ ...prev, qty: value }));
  }, []);

  const setTaxInput = useCallback((value: string) => {
    setInputs((prev) => ({ ...prev, tax: value }));
  }, []);

  const setDiscountInput = useCallback((value: string) => {
    setInputs((prev) => ({ ...prev, discount: value }));
  }, []);

  const setSpecialDiscountInput = useCallback((value: string) => {
    setInputs((prev) => ({ ...prev, specialDiscount: value }));
  }, []);

  const setBonusInput = useCallback((value: string) => {
    setInputs((prev) => ({ ...prev, bonus: value }));
  }, []);

  const setBnoInput = useCallback((value: string) => {
    setInputs((prev) => ({ ...prev, bno: value }));
  }, []);

  const setExpireDate = useCallback((value: string) => {
    setInputs((prev) => ({ ...prev, expireDate: value }));
  }, []);

  // Map all handlers to their respective fields
  return {
    // State
    qtyInput,
    setQtyInput,
    taxInput,
    setTaxInput,
    discountInput,
    setDiscountInput,
    specialDiscountInput,
    setSpecialDiscountInput,
    bonusInput,
    setBonusInput,
    bnoInput,
    setBnoInput,
    expireDate,
    setExpireDate,

    // Refs
    qtyRef,
    taxRef,
    discountRef,
    specialDiscountRef,
    bonusRef,
    bnoRef,
    expireDateRef,
    submitButtonRef,

    // Handlers
    handleQtyChange: handleChange('qty'),
    handleQtyBlur,
    handleQtyKeyDown,
    handleTaxChange: handleChange('tax'),
    handleTaxBlur,
    handleTaxKeyDown,
    handleDiscountChange: handleChange('discount'),
    handleDiscountBlur,
    handleDiscountKeyDown,
    handleSpecialDiscountChange: handleChange('specialDiscount'),
    handleSpecialDiscountBlur,
    handleSpecialDiscountKeyDown,
    handleBonusChange: handleChange('bonus'),
    handleBonusBlur,
    handleBonusKeyDown,
    handleBnoChange,
    handleBnoBlur,
    handleBnoKeyDown,
    handleExpireDateChange,
    handleExpireDateBlur,
    handleExpireDateFocus,
    handleExpireDateKeyDown,
    handleFocusSelect,

    // Navigation
    fnToGoToQty,
    fnToGoToDiscount,

    // Utilities
    resetForm,
    selectText,
  };
}
